import { decamelize } from 'humps'

import pretty from '@st-operations/uikit/lib/pretty.js'

import {
  getCustomAudience,
  getCustomAudiences,
  getTaxonomyAudience,
  updateCustomAudience,
  updateTaxonomyAudience,
} from '_services/audience'
import { getTaxonomyAudiences } from '_services/taxonomies'

import {
  FIELDS as INTEGRATION_FIELDS,
  DESTINATIONS_API_INTEGRATIONS,
} from '../audience-creator/components/selected-destinations'

/**
 * Each "collection" the bulk edit supports is described here: which endpoints
 * serve it, which fields are loaded (projection), which ones can be edited
 * (capabilities) and which statuses it accepts. Field names are camelCase —
 * the services layer camelizes responses and decamelizes payloads — and are
 * decamelized only when used as the `fields` querystring projection.
 */

export { INTEGRATION_FIELDS }

export const ALL_INTEGRATION_FIELDS = [...new Set(Object.values(INTEGRATION_FIELDS).flat())]

const FIELD_LABELS = {
  name: 'Audience Name',
  status: 'Status',
  regions: 'Regions',
  destinations: 'Destinations',
}

export const prettyField = (field) => FIELD_LABELS[field] ?? pretty(decamelize(field))

export const getAudienceIntegrations = (audience) => [
  ...new Set(
    (audience.destinations || [])
      .map(
        (destination) =>
          destination.apiIntegration || DESTINATIONS_API_INTEGRATIONS[destination.name]
      )
      .filter(Boolean)
  ),
]

export const COLLECTIONS = {
  'custom-audiences': {
    id: 'custom-audiences',
    label: 'Custom Audiences',
    statuses: ['draft', 'approved', 'archived', 'processed', 'deleted'],
    capabilities: {
      status: true,
      regions: true,
      destinations: true,
      integrationFields: true,
    },
    projection: [
      '_id',
      'name',
      'status',
      'regions',
      'destinations',
      'updatedAt',
      'createdBy',
      'updatedBy',
      ...ALL_INTEGRATION_FIELDS,
    ],
    // explicit sort keeps the page-by-page load consistent
    defaultSort: 'updated_at,-1',
    // detail page for a single audience (opened from the edit/review/run stages)
    audienceUrl: (id) => `/audience-creator/${id}`,
    fetchPage: (context, pageParams) => getCustomAudiences({ ...context.filters, ...pageParams }),
    fetchById: (id) => getCustomAudience(id),
    // bulk-load a set of ids through the list endpoint (one request per page)
    // instead of one request per id — used to reload/retry sessions fast
    fetchByIds: (ids, pageParams) => getCustomAudiences({ _id: ids, ...pageParams }),
    // The custom-audiences PUT (datasphere-api crud middleware) loads the current
    // document and applies a `$set` merge, so sending only the changed fields is
    // safe — unsent fields (filters/predictors, etc.) are preserved server-side.
    update: (id, body) => updateCustomAudience(id, body),
  },
  'taxonomy-audiences': {
    id: 'taxonomy-audiences',
    label: 'Syndicated Audiences',
    statuses: ['draft', 'submitted', 'processed', 'approved', 'archived', 'expired'],
    // regions and destinations are controlled at the taxonomy level
    capabilities: {
      status: true,
      regions: false,
      destinations: false,
      integrationFields: false,
    },
    projection: ['_id', 'name', 'status', 'updatedAt', 'taxonomy'],
    defaultSort: 'name,1',
    // detail page for a single syndicated audience
    audienceUrl: (id) => `/audience/${id}`,
    fetchPage: (context, pageParams) =>
      getTaxonomyAudiences(context.taxonomy.slug, { ...context.filters, ...pageParams }),
    fetchById: (id) => getTaxonomyAudience(id),
    // Unlike custom audiences, the taxonomy PUT (datasphere-api's /audiences/taxonomy
    // proxy to the DMP) does NOT merge: it rebuilds `datasphere` from the request
    // body, defaulting every absent filter field (predictors, topics, ages, …) to
    // empty. Sending only the changed fields would therefore WIPE all filters. So
    // re-send the current, full audience with the changes merged on top — the same
    // full-object round-trip the single-audience save relies on (the GET flattens
    // `datasphere.*` to top-level, which is exactly the shape the PUT rebuilds from).
    update: async (id, body) => {
      const current = await getTaxonomyAudience(id)
      return updateTaxonomyAudience(id, { ...current, ...body })
    },
    // The /audiences/taxonomy endpoint answers through a data-api proxy that
    // always resolves with HTTP 200 and wraps the real result in an envelope
    // ({ statusCode, statusMessage, body }). A logical failure (e.g. an expired
    // token returning 401 with body.error) would otherwise look like a success,
    // so we surface it as an error the run stage can catch.
    getUpdateError: (response) => {
      const status = response?.statusCode
      const error = response?.body?.error
      if (error) return error
      if (status && (status < 200 || status >= 300)) {
        return response?.statusMessage || `Update failed (${status})`
      }
      return null
    },
  },
}

export const getCollection = (id) => COLLECTIONS[id] ?? COLLECTIONS['custom-audiences']
